import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SseMessage } from './sseClient';

/**
 * SSE connection state + event log. Mirrors * `store/reducers/sse.js` behavior:
 * - Bounded buffer of received messages (last 200).
 * - `connected` toggles around the live transport.
 * - `authError` is cleared automatically on auth-state changes (login/refresh)
 * by external reducers; here we expose explicit reducers for the same.
 */

export interface OrderUpdatePayload {
  OrderId?: string;
  NewStatus?: string;
  SafeId?: string;
  Source?: string;
  ExpiresAt?: string;
  [key: string]: unknown;
}

/**
 * Transport lifecycle as the UI sees it:
 * - `idle` — no connection is active or wanted (before the first start, or
 *   after a deliberate stop). The status chip hides in this state.
 * - `connecting` — the first dial after `sseStart` is in flight (no open or
 *   error seen yet).
 * - `connected` — stream open, events flowing.
 * - `reconnecting` — transport dropped; a backoff retry is armed (or an
 *   immediate reconnect is dialling).
 * - `off` — every retry failed; the middleware gave up. Only an explicit
 *   `sseReconnect` (or page reload) recovers from here.
 * Auth failures are tracked separately in `authError` — they gate the whole
 * feature (retrying can't help a 401/403), not just the current connection.
 */
export type SseConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'off';

export interface SseState {
  connected: boolean;
  status: SseConnectionStatus;
  /** Current backoff attempt ordinal while `reconnecting`, else null. */
  retryAttempt: number | null;
  /** Total attempts the client will make before giving up, else null. */
  maxRetries: number | null;
  /** ISO timestamp of the last received envelope (any EventType). */
  lastEventAt: string | null;
  /** ISO timestamp of the most recent successful stream open. */
  connectedAt: string | null;
  messages: SseMessage[];
  authError: string | null;
  verboseLogging: boolean;
}

const MAX_MESSAGES = 200;

const initialState: SseState = {
  connected: false,
  status: 'idle',
  retryAttempt: null,
  maxRetries: null,
  lastEventAt: null,
  connectedAt: null,
  messages: [],
  authError: null,
  verboseLogging: false,
};

const sseSlice = createSlice({
  name: 'sse',
  initialState,
  reducers: {
    /** Open the connection. Middleware listens for this action type. */
    sseStart: (state, action: PayloadAction<{ loggingEnabled?: boolean } | undefined>) => {
      state.verboseLogging = action.payload?.loggingEnabled ?? false;
      // The middleware dials on this action — reflect the in-flight dial so
      // the status chip shows "Connecting…" before the first open/error.
      state.connected = false;
      state.status = 'connecting';
      state.retryAttempt = null;
    },
    /** Close the connection. Middleware listens for this action type. */
    sseStop: () => {
      // Middleware handles the disconnect — the slice just resets here.
      return initialState;
    },
    sseConnected: {
      // Timestamps are stamped in `prepare` (the RTK-sanctioned home for
      // impure inputs) so the reducer itself stays pure/replayable.
      prepare: () => ({ payload: { at: new Date().toISOString() } }),
      reducer: (state, action: PayloadAction<{ at: string }>) => {
        state.connected = true;
        state.status = 'connected';
        state.retryAttempt = null;
        state.connectedAt = action.payload.at;
      },
    },
    /** Deliberate teardown (sseStop / unmount) — not a failure state. */
    sseDisconnected: (state) => {
      state.connected = false;
      state.status = 'idle';
      state.retryAttempt = null;
    },
    /** The transport dropped and the client armed a backoff retry (payload
 * carries the attempt ordinal), or an immediate reconnect is dialling
 * (no payload). */
    sseReconnecting: (
      state,
      action: PayloadAction<{ attempt: number; maxRetries: number } | undefined>,
    ) => {
      state.connected = false;
      state.status = 'reconnecting';
      state.retryAttempt = action.payload?.attempt ?? null;
      state.maxRetries = action.payload?.maxRetries ?? state.maxRetries;
    },
    /** Every backoff retry failed — live updates are off until the user asks
 * for a reconnect (or reloads). */
    sseRetriesExhausted: (state) => {
      state.connected = false;
      state.status = 'off';
      state.retryAttempt = null;
    },
    /** User-initiated immediate reconnect (status chip / hover card button).
 * Middleware calls `client.reconnectNow()`; the optimistic `reconnecting`
 * here gives instant feedback while the dial is in flight. */
    sseReconnect: (state) => {
      state.status = 'reconnecting';
      state.retryAttempt = null;
    },
    /** A parsed envelope arrived from the server. Deduped on EventId. */
    sseEventReceived: {
      prepare: (event: SseMessage) => ({
        payload: event,
        meta: { receivedAt: new Date().toISOString() },
      }),
      reducer: (
        state,
        action: PayloadAction<SseMessage, string, { receivedAt: string }>,
      ) => {
        const event = action.payload;
        if (!event) return;
        state.lastEventAt = action.meta.receivedAt;
        if (event.EventId && state.messages.some((m) => m?.EventId === event.EventId)) {
          return;
        }
        state.messages.push(event);
        if (state.messages.length > MAX_MESSAGES) {
          state.messages.splice(0, state.messages.length - MAX_MESSAGES);
        }
      },
    },
    sseAuthError: (state, action: PayloadAction<string | undefined>) => {
      state.connected = false;
      state.status = 'off';
      state.retryAttempt = null;
      state.authError = action.payload ?? 'Authorization failed';
    },
    /** Clear the auth-error flag — call this when fresh credentials arrive
 * so the next `sseStart` can actually retry. */
    sseAuthErrorCleared: (state) => {
      state.authError = null;
    },
  },
});

export const {
  sseStart,
  sseStop,
  sseConnected,
  sseDisconnected,
  sseReconnecting,
  sseRetriesExhausted,
  sseReconnect,
  sseEventReceived,
  sseAuthError,
  sseAuthErrorCleared,
} = sseSlice.actions;

export const sseReducer = sseSlice.reducer;

// --- selectors ---

import type { RootState } from '@app/store';

export const selectSseConnected = (state: RootState): boolean => state.sse.connected;
export const selectSseStatus = (state: RootState): SseConnectionStatus => state.sse.status;
export const selectSseRetryAttempt = (state: RootState): number | null => state.sse.retryAttempt;
export const selectSseMaxRetries = (state: RootState): number | null => state.sse.maxRetries;
export const selectSseLastEventAt = (state: RootState): string | null => state.sse.lastEventAt;
export const selectSseConnectedAt = (state: RootState): string | null => state.sse.connectedAt;
export const selectSseAuthError = (state: RootState): string | null => state.sse.authError;
export const selectSseMessages = (state: RootState): SseMessage[] => state.sse.messages;
export const selectSseLastMessage = (state: RootState): SseMessage | undefined =>
  state.sse.messages[state.sse.messages.length - 1];
